import { ApiCallError } from "Infrastructure/Api/ApiUtils";
import i18n from "i18next";

const STATUS_KEY: Record<number, string> = {
	0: "Errors:Server.Network",
	400: "Errors:Server.BadRequest",
	401: "Errors:Server.Unauthorized",
	403: "Errors:Server.Forbidden",
	404: "Errors:Server.NotFound",
	409: "Errors:Server.Conflict",
	429: "Errors:Server.TooManyRequests",
	500: "Errors:Server.InternalError",
	503: "Errors:Server.ExternalUnavailable",
};

const FALLBACK_KEY = "Errors:Server.Unknown";

function statusKey(status: number): string {
	const known = STATUS_KEY[status];
	if (known !== undefined) return known;
	if (status >= 500) return "Errors:Server.InternalError";
	if (status >= 400) return "Errors:Server.BadRequest";
	return FALLBACK_KEY;
}

/**
 * Maps an API error to a user-facing Czech message.
 *
 * Resolution ladder:
 *   1. First error's `reason` → `Errors:Reasons.<reason>` (cross-feature business reasons).
 *   2. First error's `message` (FluentValidation already provides a usable text from BE).
 *   3. HTTP status → `Errors:Server.<status>`.
 *   4. Generic fallback.
 *
 * For inline form errors prefer feature-scoped translation via `useHandleServerError`
 * with a feature-specific resource (e.g. `Resources.SignIn.Error`) — this helper is for
 * global toast / unhandled error contexts.
 */
export function translateApiError(error: unknown): string {
	if (!(error instanceof ApiCallError)) {
		return i18n.t(FALLBACK_KEY);
	}

	const firstError = error.body?.errors
		? Object.values(error.body.errors)[0]?.[0]
		: undefined;

	if (firstError?.reason) {
		// Same ladder as useServerError, minus the feature scope (we don't know it here).
		const candidates = [
			`Errors:Reasons.${firstError.reason}`,
			`Validation:${firstError.reason}`,
		];
		const params =
			(firstError.params as Record<string, unknown> | null | undefined) ??
			undefined;
		for (const key of candidates) {
			if (i18n.exists(key)) {
				return i18n.t(key, params ?? {});
			}
		}
	}

	if (firstError?.message) {
		return firstError.message;
	}

	return i18n.t(statusKey(error.status));
}
